<template>
<article>
    <p>
        Instrumenting a Java application with OpenTelemetry can provide valuable insights into the application's performance and behavior.
        OpenTelemetry is an open-source observability framework that enables the collection and exporting of telemetry data.
        This document covers the steps required to instrument a Java application with OpenTelemetry.
    </p>

    <h2 id="automatic">Automatic instrumentation</h2>

    <p>
        Let's start with a simple Spring Boot web application.
    </p>

    <pre v-hl.java>
@SpringBootApplication
@RestController
public class DemoApplication {
	public static void main(String[] args) {
		SpringApplication.run(DemoApplication.class, args);
	}

	@GetMapping("/hello/{name}")
	public String hello(@PathVariable(value = "name") String name) {
		return String.format("Hello, %s!", name);
	}
}
    </pre>

    <p>
        OpenTelemetry instrumentation generates detailed spans that describe the handling of inbound HTTP requests.
        These spans will provide insight into the entire lifecycle of each request, from the moment it arrives at the
        server to the moment it is sent back to the client.
    </p>

    <p>
        OpenTelemetry provides a Java agent that automatically detects and instruments the most popular
        application servers, clients, and frameworks.
        This means we don't even need to change the code of our app to instrument it.
    </p>

    <pre v-hl.sh>
# download the latest version of the agent
wget https://github.com/open-telemetry/opentelemetry-java-instrumentation/releases/latest/download/opentelemetry-javaagent.jar
    </pre>

    <p>
        Then, run the application with the instrumentation:
    </p>

    <pre v-hl.sh>
export \
 OTEL_SERVICE_NAME="spring-demo" \
 OTEL_EXPORTER_OTLP_TRACES_ENDPOINT="http://coroot.coroot:8080/v1/traces" \
 OTEL_EXPORTER_OTLP_TRACES_PROTOCOL="http/protobuf" \
 OTEL_METRICS_EXPORTER="none" \
 && java -javaagent:./opentelemetry-javaagent.jar -jar build/libs/demo-0.0.1-SNAPSHOT.jar
    </pre>


    <p>
        As a result, our app reports traces to the configured OpenTelemetry collector:
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/simple_app_trace.png" />
    </v-card>


    <p>
        Each server span contains all the details related to a given request:
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/server_span_attributes.png" />
    </v-card>

    <h2 id="exceptions">Exceptions</h2>

    <p>
        Now, let's explore what happens when our app throws an exception.
    </p>

    <pre v-hl.java>
@SpringBootApplication
@RestController
public class DemoApplication {
	public static void main(String[] args) {
		SpringApplication.run(DemoApplication.class, args);
	}

	@GetMapping("/hello/{name}")
	public String hello(@PathVariable(value = "name") String name) {
		throw new ResponseStatusException(HttpStatus.INTERNAL_SERVER_ERROR, "Failure injection: HTTP-500");
	}
}
    </pre>

    <p>
        The resulting trace indicates two spans with errors.
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/error_trace.png" />
    </v-card>

    <p>
        Typically, the error is captured within the details of the deepest nested failing span.
        In this particular case, this is the <var>DemoApplication.hello</var> span.
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/error_span_attributes.png" />
    </v-card>

    <p>
        As you can see, we have easily identified the reason why this particular request resulted in an error.
    </p>

    <h2 id="database">Database calls</h2>

    <p>Now, let's add a database call to our app:</p>

    <pre v-hl.java>
@SpringBootApplication
@RestController
public class DemoApplication {
	@Autowired
	private UserRepository userRepository;

	public static void main(String[] args) {
		SpringApplication.run(DemoApplication.class, args);
	}

	@GetMapping("/hello/{id}")
	public String hello(@PathVariable(value = "id") Integer id) {
		Optional&lt;User&gt; user = userRepository.findById(id);
		if (user.isEmpty()) {
			throw new ResponseStatusException(HttpStatus.NOT_FOUND, "User not found");
		}

		return String.format("Hello, %s!", user.get().getName());
	}
}
    </pre>

    <p>
        Once again, there is no need to add any additional instrumentation as the OTel Java agent automatically captures
        every database call.
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/trace_with_db_call.png" />
    </v-card>

    <p>Span attributes:</p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/db_call_span_attributes.png" />
    </v-card>

    <h2 id="http_calls">HTTP calls and context propagation</h2>

    <p>
        Next, instead of retrieving a user from the database, let's make an HTTP call to a service and retrieve a JSON
        response containing the desired information.
    </p>

    <pre v-hl.java>
@SpringBootApplication
@RestController
public class DemoApplication {
	public static void main(String[] args) {
		SpringApplication.run(DemoApplication.class, args);
	}

	@GetMapping("/hello/{id}")
	public String hello(@PathVariable(value = "id") Integer id) {
		RestTemplate tpl = new RestTemplate();
        tpl.getMessageConverters().add(new MappingJackson2HttpMessageConverter());

        User user = tpl.getForObject(String.format("http://127.0.0.1:8082/user/%d", id), User.class);

		return String.format("Hello %s!", user.getName());
	}
}
    </pre>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/trace_with_http_call.png" />
    </v-card>

    <p>Client span attributes:</p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/http_call_span_attributes.png" />
    </v-card>

    <p>
        As you can see, the resulting trace includes a span reported by the <var>user</var> service.
        Both services are instrumented with OpenTelemetry.
        But how is the context of the current trace propagated between them?
        To gain a better understanding of context propagation, let's examine the request sent to the <var>user</var> service:
    </p>

    <pre v-hl.sh>
GET /user/1 HTTP/1.1
Host: 127.0.0.1
Connection: keep-alive
User-agent: Java/17.0.1
Accept: application/json
Traceparent: 00-d12946898a11d917a2fb6bd1ab054e0e-d55429431be788ff-01
    </pre>

    <p>
        OpenTelemetry adds the <a href="https://www.w3.org/TR/trace-context/#traceparent-header" target="_blank">Traceparent</a>
        HTTP header on the client side, and dependency services read this header to propagate the trace context.
        It has the following format:
    </p>

    <p><var>Version-TraceID-ParentSpanID-TraceFlags</var></p>

    <p>
        In our case, <var>d12946898a11d917a2fb6bd1ab054e0e</var> is the TraceID, and <var>d55429431be788ff</var> is the ParentSpanId.
    </p>

    <h2 id="custom_attributes">Adding custom attributes and events to spans</h2>

    <p>
        To customize OpenTelemetry spans, you will need to add the OpenTelemetry dependency to your project:
    </p>

    <v-tabs v-model="otelDependency" class="mb-4" color="blue darken-2">
        <v-tab>Gradle</v-tab>
        <v-tab>Maven</v-tab>
    </v-tabs>

    <v-tabs-items v-model="otelDependency">
        <v-tab-item transition="scale">
            <pre v-hl.gradle>
dependencies {
    implementation 'io.opentelemetry:opentelemetry-sdk:1.26.0'
}
            </pre>
        </v-tab-item>

        <v-tab-item transition="scale">
            <pre v-hl.xml>
&lt;project&gt;
    &lt;dependencyManagement&gt;
        &lt;dependencies&gt;
            &lt;dependency&gt;
                &lt;groupId&gt;io.opentelemetry&lt;/groupId&gt;
                &lt;artifactId&gt;opentelemetry-bom&lt;/artifactId&gt;
                &lt;version&gt;1.26.0&lt;/version&gt;
                &lt;type&gt;pom&lt;/type&gt;
                &lt;scope&gt;import&lt;/scope&gt;
            &lt;/dependency&gt;
        &lt;/dependencies&gt;
    &lt;/dependencyManagement&gt;

    &lt;dependencies&gt;
        &lt;dependency&gt;
            &lt;groupId&gt;io.opentelemetry&lt;/groupId&gt;
            &lt;artifactId&gt;opentelemetry-sdk&lt;/artifactId&gt;
        &lt;/dependency&gt;
    &lt;/dependencies&gt;
&lt;/project&gt;
            </pre>
        </v-tab-item>
    </v-tabs-items>

    <p>
        Then, you can retrieve the current span and set custom attributes or add events:
    </p>

    <pre v-hl.java>
@SpringBootApplication
@RestController
public class DemoApplication {
	public static void main(String[] args) {
		SpringApplication.run(DemoApplication.class, args);
	}

	@GetMapping("/hello/{id}")
	public String hello(@PathVariable(value = "id") Integer id) {
        Span span = Span.current();

		// set an attribute
		span.setAttribute("user.id", id);

		//add an event
		span.addEvent("the user profile has been loaded from the database");

        ...
		return String.format("Hello %s!", user.getName());
	}
}
    </pre>

    <p>The resulting span:</p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_java/custom_attributes_and_events.png" />
    </v-card>

</article>
</template>

<script>
export default {
    data() {
        return {
            otelDependency: null,
        };
    },
}
</script>

<style scoped>

</style>
